import { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { enqueue, flushSoon, pendingIds } from './outbox';
import { fetchAllRows } from './storage';

// ── localStorage helpers ────────────────────────────────────────────────────
function lsKey(userId) { return `aw_vocab_${userId}`; }

function lsLoad(userId) {
  try { return JSON.parse(localStorage.getItem(lsKey(userId)) || 'null') || {}; } catch { return {}; }
}

function lsSave(userId, data) {
  try { localStorage.setItem(lsKey(userId), JSON.stringify(data)); } catch {}
}

function lsSet(userId, word, entry) {
  try {
    const d = lsLoad(userId);
    d[word] = entry;
    lsSave(userId, d);
  } catch {}
}

// ── Supabase: строки user_words ↔ записи словаря ─────────────────────────────
const REVIEW_DAYS = { 1: 1, 2: 3, 3: 7, 4: 30 };

// Флаг фразы хранится в notes префиксом `[phrase]` (без миграции схемы).
function toRow(userId, word, e) {
  return {
    user_id: userId, word,
    translation: e.translation || '',
    notes: e.isPhrase ? `[phrase]${e.notes ? '\n' + e.notes : ''}` : (e.notes || ''),
    status: e.status ?? 1,
    ignored: !!e.ignored,
    word_lang: e.wordLang || '',
    next_review_date: new Date(Date.now() + (REVIEW_DAYS[e.status] ?? 1) * 86_400_000).toISOString(),
    updated_at: new Date().toISOString(),
  };
}

function fromRow(r) {
  const rawNotes = r.notes || '';
  const isPhrase = rawNotes.startsWith('[phrase]');
  return {
    status:      r.status,
    translation: r.translation || '',
    notes:       isPhrase ? rawNotes.slice('[phrase]'.length).replace(/^\n/, '') : rawNotes,
    ignored:     r.ignored     || false,
    wordLang:    r.word_lang   || '',
    isPhrase,
    _s:          1, // сервер эту запись уже видел
  };
}

// Забрать словарь с сервера целиком (постранично: больше 1000 слов раньше не
// доходило до нового устройства). Слова с неотправленными правками не трогаем.
// Слова, которых сервер никогда не видел, досылаем; которые видел, но их там
// больше нет, — удалены на другом устройстве.
async function pullWords(userId) {
  const rows = await fetchAllRows(() => supabase.from('user_words')
    .select('word, translation, notes, status, ignored, word_lang')
    .eq('user_id', userId).order('word'));
  const pending = pendingIds().words;
  const bulk = {};
  for (const r of rows) if (!pending.has(r.word)) bulk[r.word] = fromRow(r);
  const removed = [];
  for (const [w, e] of Object.entries(lsLoad(userId))) {
    if (w in bulk || pending.has(w)) continue;
    if (e._s) removed.push(w);
    else enqueue('word.upsert', 'word:' + w, toRow(userId, w, e));
  }
  flushSoon();
  return { bulk, removed };
}

// ── Pub/Sub word store ──────────────────────────────────────────────────────
function createWordStore() {
  const data     = {};           // word -> { status, translation, notes, ignored, wordLang }
  const wordSubs = new Map();    // word -> Set<() => void>
  const anySubs  = new Set();    // fires on ANY change (StatsBar, LanguageStats)

  return {
    get(word) { return data[word] ?? null; },

    set(word, entry) {
      data[word] = entry;
      wordSubs.get(word)?.forEach(cb => cb());
      anySubs.forEach(cb => cb());
    },

    remove(word) {
      if (!(word in data)) return;
      delete data[word];
      wordSubs.get(word)?.forEach(cb => cb());
      anySubs.forEach(cb => cb());
    },

    setBulk(entries) {
      for (const [w, entry] of Object.entries(entries)) {
        data[w] = entry;
        wordSubs.get(w)?.forEach(cb => cb());
      }
      anySubs.forEach(cb => cb());
    },

    subscribe(word, cb) {
      if (!wordSubs.has(word)) wordSubs.set(word, new Set());
      wordSubs.get(word).add(cb);
      return () => wordSubs.get(word)?.delete(cb);
    },

    subscribeAny(cb) {
      anySubs.add(cb);
      return () => anySubs.delete(cb);
    },

    snapshot() { return { ...data }; },
  };
}

const StoreCtx   = createContext(null);
const ActionsCtx = createContext(null);

// ── Provider ────────────────────────────────────────────────────────────────
export function WordLearningProvider({ userId, children }) {
  const storeRef = useRef(null);
  if (!storeRef.current) storeRef.current = createWordStore();

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) { setLoaded(true); return; }

    // 1. Load from localStorage immediately (instant, works offline)
    const cached = lsLoad(userId);
    if (Object.keys(cached).length) {
      storeRef.current.setBulk(cached);
    }
    // 1b. Сразу разблокируем UI ридера — кэш с прошлого захода уже даёт
    //     полный список слов. Раньше ждали ответ Supabase → ридер висел на
    //     «Loading vocabulary…» секунды на медленном интернете. Supabase
    //     ниже отрабатывает в фоне и тихо обновляет store при необходимости.
    setLoaded(true);

    // 2. Load from Supabase (may add/override, syncs across devices)
    reloadVocabRef.current?.();
  }, [userId]);

  // Save / update a word as a LingQ entry. `isPhrase=true` помечает запись
  // как многословную фразу — это нужно phraseRanges-детектору в TextReader,
  // чтобы НЕ подсвечивать обычные слова как substring-фразы. Флаг хранится
  // в notes-поле Supabase префиксом `[phrase]` (без миграции схемы); локально
  // — отдельным полем entry.isPhrase. Прозрачный round-trip через sync.
  const saveWord = useCallback(async ({ word, translation, notes, status, wordLang = '', isPhrase = false }) => {
    const w = word.toLowerCase().trim();
    const entry = { status, translation: translation ?? '', notes: notes ?? '', ignored: false, wordLang, isPhrase: !!isPhrase };

    // Update in-memory store immediately
    storeRef.current.set(w, entry);

    // Persist to localStorage immediately (survives refresh even if Supabase fails)
    lsSet(userId, w, entry);

    // На сервер — через очередь отправки (дойдёт и после офлайна или ошибки сети).
    if (userId) { enqueue('word.upsert', 'word:' + w, toRow(userId, w, entry)); flushSoon(); }
  }, [userId]);

  // Полное удаление слова/фразы: память + localStorage + Supabase. Используется
  // в WordTooltip для кнопки «🗑 Удалить» — пользователь убирает ошибочно
  // созданную фразу или передумал учить слово.
  const deleteWord = useCallback(async (word) => {
    const w = word.toLowerCase().trim();
    if (!w) return;
    // 1) in-memory store
    storeRef.current.remove(w);
    // 2) localStorage
    try {
      const d = lsLoad(userId);
      if (w in d) { delete d[w]; lsSave(userId, d); }
    } catch {}
    // 3) Supabase — через очередь отправки
    if (userId) { enqueue('word.delete', 'word:' + w, { word: w }); flushSoon(); }
  }, [userId]);

  // Mark word as ignored
  const ignoreWord = useCallback(async (word, wordLang = '') => {
    const w = word.toLowerCase().trim();
    const entry = { status: 1, translation: '', notes: '', ignored: true, wordLang };
    storeRef.current.set(w, entry);
    lsSet(userId, w, entry);
    if (userId) { enqueue('word.upsert', 'word:' + w, toRow(userId, w, entry)); flushSoon(); }
  }, [userId]);

  // Mark all State-0 (unseen) words on a page as Known when advancing pages
  const markPageAsRead = useCallback(async (pageWords, wordLang = '') => {
    const store = storeRef.current;
    const toMark = pageWords.filter(w => !store.get(w));
    if (!toMark.length) return;

    const lsData     = lsLoad(userId);

    for (const w of toMark) {
      const entry = { status: 4, translation: '', notes: '', ignored: false, wordLang };
      store.set(w, entry);
      lsData[w] = entry;
      if (userId) enqueue('word.upsert', 'word:' + w, toRow(userId, w, entry));
    }
    lsSave(userId, lsData);
    flushSoon();
  }, [userId]);

  // Ref declared BEFORE the effect that reads it — relying on declaration-order
  // was working only because refs are populated during render (which runs before
  // useEffect callbacks fire), but the original arrangement was easy to break.
  const reloadVocabRef = useRef(null);

  // Listen for global reload event triggered by manual sync button
  useEffect(() => {
    const handler = () => reloadVocabRef.current?.();
    window.addEventListener('ac_reload_vocab', handler);
    return () => window.removeEventListener('ac_reload_vocab', handler);
  }, []);

  // Force reload from Supabase (called by manual sync button)
  const reloadVocab = useCallback(async () => {
    if (!userId) return;
    let res;
    try { res = await pullWords(userId); }
    catch (e) { console.warn('[wordLearning] reload error:', e?.message); return; }
    const { bulk, removed } = res;
    if (Object.keys(bulk).length) storeRef.current.setBulk(bulk);
    removed.forEach(w => storeRef.current.remove(w));
    const merged = { ...lsLoad(userId), ...bulk };
    removed.forEach(w => { delete merged[w]; });
    lsSave(userId, merged);
  }, [userId]);

  // Keep ref current so the event listener can call the latest version
  reloadVocabRef.current = reloadVocab;

  return (
    <StoreCtx.Provider value={storeRef.current}>
      <ActionsCtx.Provider value={{ saveWord, ignoreWord, deleteWord, markPageAsRead, loaded, reloadVocab }}>
        {children}
      </ActionsCtx.Provider>
    </StoreCtx.Provider>
  );
}

// ── Core hooks ───────────────────────────────────────────────────────────────

export function useWordStatus(word) {
  const store      = useContext(StoreCtx);
  const normalized = word.toLowerCase().trim();
  const [, tick]   = useState(0);

  useEffect(() => {
    if (!store) return;
    return store.subscribe(normalized, () => tick(n => n + 1));
  }, [store, normalized]);

  return store?.get(normalized) ?? null;
}

export function useWordStore()   { return useContext(StoreCtx); }
export function useWordActions() { return useContext(ActionsCtx); }

// ── Language statistics hook ─────────────────────────────────────────────────
// Returns { fr: { total, known, learning }, de: { ... }, ... }
// Re-computes on any word change via subscribeAny.
// Возвращает true, если КАЖДОЕ слово из переданного массива уже имеет статус
// в store (т.е. страница уже была «прочитана»). Подписывается на любые
// изменения через subscribeAny, чтобы корректно реагировать когда пользователь
// помечает слово вручную. Используется TextReader-ом чтобы не показывать
// «Mark Read & Next» и не «залетать» в статистику при повторном чтении.
export function useAllMarked(words) {
  const store = useContext(StoreCtx);
  const [marked, setMarked] = useState(false);
  useEffect(() => {
    if (!store) { setMarked(false); return; }
    const calc = () => {
      if (!words || !words.length) { setMarked(false); return; }
      setMarked(words.every(w => store.get(w)));
    };
    calc();
    return store.subscribeAny(calc);
  }, [store, words]);
  return marked;
}

export function useLanguageStats() {
  const store      = useContext(StoreCtx);
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!store) return;
    const calc = () => {
      const all = store.snapshot();
      const s   = {};
      for (const entry of Object.values(all)) {
        const lang = entry.wordLang || '';
        if (!lang) continue;
        if (!s[lang]) s[lang] = { total: 0, known: 0, learning: 0 };
        s[lang].total++;
        if (entry.ignored || entry.status >= 4) s[lang].known++;
        else s[lang].learning++;
      }
      setStats(s);
    };
    calc();
    return store.subscribeAny(calc);
  }, [store]);

  return stats;
}
